import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  const reviews = t("testimonials.reviews");

  return (
    <section id="testimonials" className="py-24 bg-[#0A0A0A] relative overflow-hidden" data-testid="testimonials-section">
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#FF00FF]/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#D4AF37] uppercase mb-4 block">
            {t("testimonials.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("testimonials.title1")}</span>{" "}
            <span className="text-[#D4AF37]">{t("testimonials.title2")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t("testimonials.description")}
          </p>
        </motion.div>

        {/* Rating Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-4 bg-white/5 border border-white/10 px-8 py-4">
            <div className="text-4xl font-bold text-white">4.8</div>
            <div className="text-left">
              <div className="flex gap-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="text-gray-400 text-sm">
                {language === "tr" ? "156+ değerlendirme" : "156+ reviews"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {reviews.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white/5 border border-white/10 p-6 md:p-8 relative group hover:border-[#D4AF37]/30 transition-colors"
              data-testid={`testimonial-${index}`}
            >
              {/* Quote icon */}
              <Quote size={40} className="absolute top-6 right-6 text-white/5 group-hover:text-[#D4AF37]/20 transition-colors" />
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>

              {/* Text */}
              <p className="text-gray-300 leading-relaxed mb-6 relative z-10">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-8 mt-16 text-gray-500 text-sm"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#25D366] rounded-full"></span>
            {language === "tr" ? "7/24 WhatsApp Destek" : "24/7 WhatsApp Support"}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#D4AF37] rounded-full"></span>
            {language === "tr" ? "10+ Yıllık Deneyim" : "10+ Years Experience"}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#FF00FF] rounded-full"></span>
            {language === "tr" ? "50.000+ Mutlu Müşteri" : "50,000+ Happy Customers"}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
